import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

interface PendingCountsStore {
  // State
  pendingRegistrations: number
  pendingDeposits: number
  pendingWithdrawals: number
  pendingInquiries: number
  pendingChats: number
  pendingPartnerWithdrawals: number
  unreadChatMessages: number
  isConnected: boolean
  websocket: WebSocket | null
  isConnecting: boolean
  reconnectAttempts: number
  heartbeatInterval: NodeJS.Timeout | null
  reconnectTimeout: NodeJS.Timeout | null

  // Actions
  connectPendingCounts: (adminId: number) => void
  disconnectPendingCounts: () => void

  // Internal
  setPendingCounts: (counts: {
    pendingRegistrations?: number
    pendingDeposits?: number
    pendingWithdrawals?: number
    pendingInquiries?: number
    pendingChats?: number
    pendingPartnerWithdrawals?: number
    unreadChatMessages?: number
  }) => void
}

export const usePendingCountsStore = create<PendingCountsStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    pendingRegistrations: 0,
    pendingDeposits: 0,
    pendingWithdrawals: 0,
    pendingInquiries: 0,
    pendingChats: 0,
    pendingPartnerWithdrawals: 0,
    unreadChatMessages: 0,
    isConnected: false,
    websocket: null,
    isConnecting: false,
    reconnectAttempts: 0,
    heartbeatInterval: null,
    reconnectTimeout: null,
    
    // Connect to WebSocket
    connectPendingCounts: (adminId: number) => {
      const { websocket, isConnecting, heartbeatInterval, reconnectTimeout, reconnectAttempts } = get()

      // Prevent duplicate connections
      if (isConnecting) {
        console.log('📊 Pending counts WebSocket connection already in progress')
        return
      }

      if (websocket && websocket.readyState === WebSocket.OPEN) {
        console.log('📊 Pending counts WebSocket already connected')
        return
      }

      // Set connecting flag
      set({ isConnecting: true })

      // Clear existing timers
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval)
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
      }

      // Close existing connection
      if (websocket) {
        websocket.close()
        set({ websocket: null, isConnected: false })
      }

      // Create new WebSocket connection (admin JWT token query)
      const adminToken = localStorage.getItem('admin_token') || ''
      const wsUrl = `${(import.meta as any).env.VITE_WS_URL || ((window.location.protocol === 'https:' ? 'wss:' : 'ws:') + '//api.kryzonic.com')}/api/v1/admin/pending-counts/ws/${adminId}?token=${encodeURIComponent(adminToken)}`
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        console.log('📊 Admin pending counts WebSocket connected', { adminId, url: wsUrl })
        set({ isConnected: true, websocket: ws, isConnecting: false, reconnectAttempts: 0 })

        // Start heartbeat - ping every 30 seconds
        const interval = setInterval(() => {
          const { websocket: currentWs } = get()
          if (currentWs && currentWs.readyState === WebSocket.OPEN) {
            try {
              currentWs.send(JSON.stringify({ type: 'ping' }))
              console.log('📊 Pending counts WebSocket ping sent')
            } catch (error) {
              console.error('📊 Failed to send ping:', error)
            }
          } else {
            // Connection lost, try to reconnect
            console.log('📊 Connection lost during heartbeat, attempting reconnect...')
            clearInterval(interval)
            get().connectPendingCounts(adminId)
          }
        }, 30000) // 30 seconds

        set({ heartbeatInterval: interval })
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('📊 Pending counts WebSocket message received:', data)

          switch (data.type) {
            case 'initial':
              // Initial counts load
              console.log('📊 Initial counts received:', {
                pendingRegistrations: data.pending_registrations || 0,
                pendingDeposits: data.pending_deposits || 0,
                pendingWithdrawals: data.pending_withdrawals || 0,
                pendingInquiries: data.pending_inquiries || 0,
                pendingChats: data.pending_chats || 0,
                pendingPartnerWithdrawals: data.pending_partner_withdrawals || 0
              })
              set({
                pendingRegistrations: data.pending_registrations || 0,
                pendingDeposits: data.pending_deposits || 0,
                pendingWithdrawals: data.pending_withdrawals || 0,
                pendingInquiries: data.pending_inquiries || 0,
                pendingChats: data.pending_chats || 0,
                pendingPartnerWithdrawals: data.pending_partner_withdrawals || 0,
                unreadChatMessages: data.unread_chat_messages || 0
              })
              console.log('📊 Pending counts state updated after initial load')
              break

            case 'update':
              // Updated counts
              const updates: any = {}
              if (data.pending_registrations !== undefined) updates.pendingRegistrations = data.pending_registrations
              if (data.pending_deposits !== undefined) updates.pendingDeposits = data.pending_deposits
              if (data.pending_withdrawals !== undefined) updates.pendingWithdrawals = data.pending_withdrawals
              if (data.pending_inquiries !== undefined) updates.pendingInquiries = data.pending_inquiries
              if (data.pending_chats !== undefined) updates.pendingChats = data.pending_chats
              if (data.pending_partner_withdrawals !== undefined) updates.pendingPartnerWithdrawals = data.pending_partner_withdrawals
              if (data.unread_chat_messages !== undefined) updates.unreadChatMessages = data.unread_chat_messages

              console.log('📊 Pending counts update received:', updates)
              set(updates)
              console.log('📊 Pending counts state updated after update message')
              break

            case 'pong':
              // Connection heartbeat response
              console.log('📊 Pending counts WebSocket pong received')
              break

            default:
              console.warn('Unknown pending counts message type:', data.type)
          }
        } catch (error) {
          console.error('Failed to parse pending counts message:', error)
        }
      }

      ws.onclose = () => {
        console.log('📊 Admin pending counts WebSocket disconnected')
        const { heartbeatInterval: currentInterval } = get()

        if (currentInterval) {
          clearInterval(currentInterval)
        }

        set({ isConnected: false, websocket: null, isConnecting: false, heartbeatInterval: null })

        // Exponential backoff: 3s, 6s, 12s, 24s, max 30s
        const currentAttempts = get().reconnectAttempts
        const delay = Math.min(3000 * Math.pow(2, currentAttempts), 30000)

        console.log(`📊 Attempting to reconnect in ${delay / 1000}s (attempt #${currentAttempts + 1})`)

        const timeout = setTimeout(() => {
          set({ reconnectAttempts: currentAttempts + 1 })
          get().connectPendingCounts(adminId)
        }, delay)

        set({ reconnectTimeout: timeout })
      }

      ws.onerror = (error) => {
        console.error('📊 Admin pending counts WebSocket error:', error, 'URL:', wsUrl, 'adminId:', adminId)
        set({ isConnected: false, isConnecting: false })
      }
    },
    
    // Disconnect WebSocket
    disconnectPendingCounts: () => {
      const { websocket, heartbeatInterval, reconnectTimeout } = get()

      // Clear all timers
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval)
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
      }

      if (websocket) {
        websocket.close()
      }

      set({
        websocket: null,
        isConnected: false,
        isConnecting: false,
        heartbeatInterval: null,
        reconnectTimeout: null,
        reconnectAttempts: 0
      })
    },
    
    // Internal: Set pending counts
    setPendingCounts: (counts) => {
      set((state) => {
        const newState = {
        ...state,
        ...counts
        }
        console.log('📊 setPendingCounts called:', counts, 'new state:', newState)
        return newState
      })
    }
  }))
)